/**
 * DOG OWNED — the saved-pack return state, outside the builder.
 *
 * A restrained "Alfie's pack is waiting" on the homepage, the collections index
 * and retail collections. Enhancement only: the surface ships hidden and there
 * is nothing to resume without JavaScript, because the draft lives on the device
 * and never reaches Liquid.
 *
 * One controller, two journeys. It owns no parsing: pack-draft.js
 * (window.PackDraft) is the only thing that reads, validates or writes a pack
 * draft, and a node names which store it speaks for through data-pack-store —
 * the default everyday store (dog_owned_pack_v1) or Puppy's First Pack
 * (dog_owned_puppy_pack_v1), both reached through the one validated parser via
 * createStore. A second parser would be a second set of rules about what counts
 * as valid, and they would drift. dog-profile.js (window.DogProfile) is the only
 * thing that reads the profile. If either is absent, nothing happens.
 *
 * Nothing here is written as markup. The dog's name is customer input and is
 * only ever assigned through textContent. No price, count or availability is
 * ever shown — the builder revalidates those on return.
 */
(function () {
  'use strict';

  var nodes = document.querySelectorAll('[data-pack-state]');
  var prompts = document.querySelectorAll('[data-pack-prompt]');
  if ((!nodes.length && !prompts.length) || !window.PackDraft) return;

  /** The store a node speaks for. Absent or the everyday key → the default
   *  window.PackDraft, unchanged for every existing caller. Any other key →
   *  a sibling store on the same validated parser (never a second parser). */
  function storeFor(node) {
    var key = node.getAttribute('data-pack-store') || 'dog_owned_pack_v1';
    if (key === 'dog_owned_pack_v1') return window.PackDraft;
    return window.PackDraft.createStore({ key: key, journey: node.getAttribute('data-journey') || '' });
  }

  function readProfile() { return window.DogProfile && window.DogProfile.read(); }
  function isPuppyProfile(p) { return !!(p && p.life_stage === 'puppy'); }

  /** Add params to the builder URL without assuming what it already carries
   *  (during development it holds ?view=; at launch it will not). */
  function withQuery(base, pairs) {
    if (!base) return base;
    var parts = base.split('?');
    var params = new URLSearchParams(parts[1] || '');
    Object.keys(pairs).forEach(function (k) { if (pairs[k]) params.set(k, pairs[k]); });
    var q = params.toString();
    return parts[0] + (q ? '?' + q : '');
  }

  function fromTemplate(tpl, name) {
    return (name && tpl) ? tpl.split('@@').join(name) : '';
  }

  function ariaFrom(el, name) {
    if (!el) return;
    var named = el.getAttribute('data-aria-named');
    var plain = el.getAttribute('data-aria-plain');
    var value = (name && named) ? named.split('@@').join(name) : plain;
    if (value) el.setAttribute('aria-label', value);
  }

  /** The generic builder invitation this saved state supersedes, if any. */
  function inviteFor(node) {
    var wrap = node.closest('[data-pack-feature]');
    return wrap ? wrap.querySelector('[data-pack-invite]') : null;
  }

  function hide(node) {
    node.hidden = true;
    var invite = inviteFor(node);
    if (!invite) return;
    // Only bring the invitation back if no OTHER saved-pack surface in this
    // feature is currently showing. Two states can share one wrapper (the
    // homepage everyday + puppy pair): a lower-priority state that defers must
    // not undo the higher-priority state's suppression of the invitation.
    var wrap = node.closest('[data-pack-feature]');
    var anyShown = wrap && Array.prototype.some.call(
      wrap.querySelectorAll('[data-pack-state]'),
      function (s) { return s !== node && !s.hidden; }
    );
    if (!anyShown) invite.hidden = false;
  }

  function reveal(node) {
    node.hidden = false;
    var invite = inviteFor(node);
    if (invite) invite.hidden = true;
  }

  function render(node) {
    var Draft = storeFor(node);
    var journey = node.getAttribute('data-journey') || '';
    var draft = Draft.read();
    // Not a pack worth returning to → say nothing, leave the generic invite.
    if (!Draft.isResumable(draft)) { hide(node); return; }

    // Priority deferral: a higher-priority store's resumable draft owns this slot,
    // so this one stays hidden — the homepage puppy state defers to the everyday
    // pack. The collection puppy state names no deferral and always offers resume.
    var deferTo = node.getAttribute('data-defer-to');
    if (deferTo) {
      var Higher = (deferTo === 'dog_owned_pack_v1') ? window.PackDraft : window.PackDraft.createStore({ key: deferTo });
      if (Higher.isResumable(Higher.read())) { hide(node); return; }
    }

    // Puppy's First Pack presents from the draft's OWN snapshot and never reads
    // the shared profile: an adult saved dog cannot relabel it and a different
    // puppy cannot transfer it. The everyday pack keeps its profile-aware
    // "different dog -> neutral review" behaviour unchanged.
    var profile = journey === 'puppy' ? null : readProfile();
    var matches = journey === 'puppy' ? true : (!profile || Draft.matchesProfile(draft, profile));

    // A placement may surface only a pack that belongs to the current dog. That
    // gate is profile-based, so it applies to the everyday journey only; a valid
    // puppy draft shows on its own terms (Step 8/9 isolation).
    if (journey !== 'puppy' && node.hasAttribute('data-require-match') && !(profile && matches)) { hide(node); return; }

    var heading = node.querySelector('[data-pack-heading]');
    var support = node.querySelector('[data-pack-support]');
    var primary = node.querySelector('[data-pack-primary]');
    var startnew = node.querySelector('[data-pack-startnew]');
    var clear = node.querySelector('[data-pack-clear]');
    var url = node.getAttribute('data-pack-url');

    if (matches) {
      // The pack belongs to the dog on this device (or there is no current dog to
      // disagree). Name it from the draft's own snapshot.
      var name = (draft.profile_snapshot && draft.profile_snapshot.name) || '';
      if (heading) {
        heading.textContent = name
          ? fromTemplate(heading.getAttribute('data-heading-named'), name)
          : (heading.getAttribute('data-heading-plain') || '');
      }
      if (support) support.hidden = false;
      if (primary) {
        primary.textContent = name
          ? fromTemplate(primary.getAttribute('data-label-named'), name)
          : (primary.getAttribute('data-label-plain') || '');
        primary.setAttribute('href', url); // plain destination → the builder resumes
      }
      if (startnew) {
        startnew.hidden = false;
        startnew.textContent = startnew.getAttribute('data-label-plain') || '';
        startnew.setAttribute('href', url);
      }
      ariaFrom(clear, name);
    } else {
      // A pack for a different dog than the one now saved. Never claim it belongs
      // to the current dog, and do not expose the old dog's name here.
      var current = (profile && profile.name) || '';
      if (heading) heading.textContent = heading.getAttribute('data-heading-neutral') || '';
      if (support) support.hidden = true; // "pick up where you left off" is not honest for another dog
      if (primary) {
        primary.textContent = primary.getAttribute('data-label-review') || '';
        primary.setAttribute('href', withQuery(url, { pack: 'review' }));
      }
      if (startnew) {
        // "Start a pack for Max" only when starting for the current dog is
        // truthful. For Puppy's First Pack that means the current dog is a puppy —
        // an adult or senior is never offered a first-pack action, and their name
        // never appears in puppy wording. The everyday journey has no such gate.
        var offerCurrent = current && (journey !== 'puppy' || isPuppyProfile(profile));
        if (offerCurrent) {
          startnew.hidden = false;
          startnew.textContent = fromTemplate(startnew.getAttribute('data-label-current'), current);
          startnew.setAttribute('href', url);
        } else if (journey === 'puppy') {
          // Adult/senior current dog: review neutrally, no "start" action at all.
          startnew.hidden = true;
        } else {
          startnew.hidden = false;
          startnew.textContent = startnew.getAttribute('data-label-plain') || '';
          startnew.setAttribute('href', url);
        }
      }
      ariaFrom(clear, ''); // plain "Clear this saved pack from this device"
    }

    reveal(node);
  }

  /** Clearing removes only this store's key; the profile and unrelated storage
   *  are the customer's. The visible state goes at once and the removal is
   *  announced; the generic invite and saved-dog card return. */
  function doClear(node) {
    storeFor(node).forget();
    var body = node.querySelector('.pack-state__body');
    var actions = node.querySelector('[data-pack-actions]');
    var confirm = node.querySelector('[data-pack-confirm]');
    var live = node.querySelector('[data-pack-live]');
    if (body) body.hidden = true;
    if (actions) actions.hidden = true;
    if (confirm) confirm.hidden = true;
    if (live) live.textContent = node.getAttribute('data-cleared') || 'Saved pack cleared.';
    // The pack is gone: bring back the generic invite and let the saved-dog card
    // reappear (priority now: profile, then generic invite).
    var invite = inviteFor(node);
    if (invite) {
      invite.hidden = false;
      var focusable = invite.querySelector('a, button');
      if (focusable) focusable.focus();
    }
    document.dispatchEvent(new CustomEvent('pack-state:changed'));
  }

  function wire(node) {
    var clearBtn = node.querySelector('[data-pack-clear]');
    var confirm = node.querySelector('[data-pack-confirm]');
    var actions = node.querySelector('[data-pack-actions]');
    var yes = node.querySelector('[data-pack-clear-confirm]');
    var no = node.querySelector('[data-pack-clear-cancel]');
    var startnew = node.querySelector('[data-pack-startnew]');

    if (clearBtn && confirm && actions) {
      clearBtn.addEventListener('click', function () {
        actions.hidden = true;
        confirm.hidden = false;
        if (yes) yes.focus();
      });
    }
    if (no && confirm && actions) {
      no.addEventListener('click', function () {
        confirm.hidden = true;
        actions.hidden = false;
        if (clearBtn) clearBtn.focus();
      });
    }
    if (yes) yes.addEventListener('click', function () { doClear(node); });

    // Start a new pack (or "Start a pack for <current>"): clear only this store's
    // draft, keep the profile, then open the builder fresh — never silently reuse
    // the old selections. It stays a real link, so it works if the click is missed.
    if (startnew) {
      startnew.addEventListener('click', function (e) {
        e.preventDefault();
        // Replacing a resumable draft is destructive on this device; a journey may
        // ask for a calm confirmation first (Puppy's First Pack does).
        var confirmMsg = node.getAttribute('data-start-confirm');
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        storeFor(node).forget();
        document.dispatchEvent(new CustomEvent('pack-state:changed'));
        window.location.href = startnew.getAttribute('href') || node.getAttribute('data-pack-url');
      });
    }
  }

  /**
   * The retail-collection prompt: a compact "Need more than one thing? Build an
   * everyday pack" that becomes "Continue <name>'s pack" once a matching draft
   * exists. It only ever swaps the label and keeps the plain builder link — it
   * shows no prices, counts or availability, creates no selection, and never
   * offers to "continue" a pack for a different dog (that would resume into an
   * invalidation), leaving the plain build invitation instead.
   */
  function renderPrompt(node) {
    var Draft = storeFor(node);
    var draft = Draft.read();
    if (!Draft.isResumable(draft)) return;
    var profile = readProfile();
    if (profile && !Draft.matchesProfile(draft, profile)) return;

    var cta = node.querySelector('[data-pack-prompt-cta]');
    if (!cta) return;
    var name = (draft.profile_snapshot && draft.profile_snapshot.name) || '';
    cta.textContent = name
      ? fromTemplate(node.getAttribute('data-continue-named'), name)
      : (node.getAttribute('data-continue-plain') || cta.textContent);
    cta.setAttribute('href', node.getAttribute('data-pack-url'));
  }

  // Puppy's First Pack entry points are no longer personalised from the shared
  // profile. The Puppy-collection invitation CTA stays its generic, server-
  // rendered self, and no homepage puppy action appears merely because the most
  // recent guest profile happens to be a puppy (Step 9 isolation). A saved puppy
  // *draft* still surfaces named from its own snapshot through the puppy
  // pack-state above; a valid puppy profile earns only the optional shortcut
  // inside the journey itself.

  Array.prototype.forEach.call(nodes, function (node) { wire(node); render(node); });
  Array.prototype.forEach.call(prompts, function (node) { renderPrompt(node); });
})();
